import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { HospitalCard } from "@/components/HospitalCard";
import { SearchBarWithAutocomplete } from "@/components/SearchBarWithAutocomplete";
import { MedicalCodeSearch } from "@/components/MedicalCodeSearch";
import { ScenarioSearch } from "@/components/ScenarioSearch";
import { LiveCounter } from "@/components/LiveCounter";
import BackgroundMedia from "@/components/BackgroundMedia";
import { WorstOffendersSection } from "@/components/WorstOffendersSection";
import { PricingAnalyticsSummary } from "@/components/PricingAnalyticsSummary";
import { ScenariosPreview } from "@/components/ScenariosPreview";
import { RecentGuidesSwiper } from "@/components/RecentGuidesSwiper";
import { sampleHospitals, sampleComparisons, dailyOvercharges, topOffenders } from "@/data/sampleData";
import { ArrowRight, Zap, Database, ShieldAlert, MapPin, Info, TrendingUp, RefreshCw } from "lucide-react";
import heroImage from "@/assets/hero-cyberpunk-medical.jpg";
import { useBrand } from "@/config/brands";
import { HomeSEO } from "@/components/SEOHead";
import { BrandTheme } from "@/components/BrandTheme";
import { AntiGreedGuidebook } from "@/components/AntiGreedGuidebook";
import { ConsumerHomepage } from "@/components/ConsumerHomepage";
import { AdSlot } from "@/components/AdSlot";
import { DataSourceAttribution } from "@/components/DataSourceAttribution";

const Index = () => {
  const brand = useBrand();
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [currentAdSet, setCurrentAdSet] = useState(0);
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
  const [showInstallButton, setShowInstallButton] = useState(false);
  const [showAppNotifyModal, setShowAppNotifyModal] = useState(false);
  const [notifyEmail, setNotifyEmail] = useState('');
  const [isPWA, setIsPWA] = useState(false);

  // Show consumer homepage for FairPrice/American Hospital Prices brand
  if (brand.key === 'ahp') {
    return (
      <>
        <BrandTheme />
        <HomeSEO />
        <ConsumerHomepage />
      </>
    );
  }

  // Show AntiGreed Guidebook for AntiGreed brand
  if (brand.key === 'antigreed') {
    return (
      <>
        <BrandTheme />
        <HomeSEO />
        <AntiGreedGuidebook
          backgroundVideoUrl="https://storage.googleapis.com/hall-of-shame/bg/Gen-4%20Static%20shot%20ornate%20green%20book%2C%20gold%20letters%20glint%20and%20shimmer%2C%20subtle%20sparkles%20and%20soft%20glow%203221640620%204K.mp4"
        />
      </>
    );
  }

  // Fetch followed hospitals from database
  const { data: followedHospitals = [], isLoading: followedLoading, error: followedError } = useQuery({
    queryKey: ['followed-hospitals'],
    queryFn: async () => {
      console.log('Fetching followed hospitals...');
      const { data, error } = await supabase
        .from('hospitals')
        .select('*')
        .eq('followed', true)
        .order('shame_score', { ascending: false });
      
      if (error) {
        console.error('Error fetching followed hospitals:', error);
        throw error;
      }
      
      console.log('Followed hospitals data:', data);
      
      // Transform database hospitals to match the format expected by HospitalCard
      const transformed = data.map(hospital => ({
        id: hospital.id,
        name: hospital.name,
        city: hospital.city,
        state: hospital.state,
        shameScore: hospital.shame_score || 0,
        avgMarkup: hospital.avg_markup_percentage || 0,
        outlierPercentage: 0, // This should be calculated from ranking data
        totalProcedures: hospital.total_procedures || 0,
        obfuscationScore: hospital.data_obfuscation_score || 0,
        worstOvercharge: {
          procedure: "Analysis pending...",
          price: 0,
          markup: 0
        }
      }));
      
      console.log('Transformed followed hospitals:', transformed);
      return transformed;
    }
  });

  // Log for debugging
  console.log('Followed hospitals state:', { followedHospitals, followedLoading, followedError });

  // Combine fake hospitals with followed hospitals
  const allWorstOffenders = [...topOffenders, ...followedHospitals];
  console.log('All worst offenders:', allWorstOffenders);

  // Different sets of ads to cycle through
  const adSets = [
    // Tech/Software Set
    [
      {
        href: "#",
        title: "CloudSync Pro",
        description: "Backup your medical records securely",
        price: "Free Trial",
        image: null,
        bgColor: "bg-gradient-to-br from-blue-500 to-purple-600",
        icon: "☁️"
      },
      {
        href: "#",
        title: "VPN Shield",
        description: "Protect your health data privacy",
        price: "$9.99/mo",
        image: null,
        bgColor: "bg-gradient-to-br from-green-500 to-teal-600",
        icon: "🛡️"
      },
      {
        href: "#",
        title: "MedTracker App",
        description: "Track symptoms & prescriptions",
        price: "$4.99",
        image: null,
        bgColor: "bg-gradient-to-br from-red-500 to-pink-600",
        icon: "📱"
      }
    ],
    // Health/Wellness Set
    [
      {
        href: "#",
        title: "Organic Vitamin D3",
        description: "Boost immunity naturally",
        price: "$24.99",
        image: null,
        bgColor: "bg-gradient-to-br from-orange-400 to-yellow-500",
        icon: "☀️"
      },
      {
        href: "#",
        title: "Sleep Aid Tea",
        description: "Natural chamomile blend",
        price: "$18.50",
        image: null,
        bgColor: "bg-gradient-to-br from-purple-400 to-indigo-500",
        icon: "🌙"
      },
      {
        href: "#",
        title: "Fitness Tracker",
        description: "Monitor heart rate & steps",
        price: "$199.99",
        image: null,
        bgColor: "bg-gradient-to-br from-cyan-400 to-blue-500",
        icon: "⌚"
      }
    ],
    // Books/Education Set
    [
      {
        href: "#",
        title: "Healthcare Handbook",
        description: "Navigate the system like a pro",
        price: "$29.99",
        image: null,
        bgColor: "bg-gradient-to-br from-amber-500 to-orange-600",
        icon: "📚"
      },
      {
        href: "#",
        title: "Medical Spanish Course",
        description: "Communicate with providers",
        price: "$49.99",
        image: null,
        bgColor: "bg-gradient-to-br from-emerald-500 to-green-600",
        icon: "🗣️"
      },
      {
        href: "#",
        title: "First Aid Certification",
        description: "Online CPR & emergency training",
        price: "$89.00",
        image: null,
        bgColor: "bg-gradient-to-br from-red-400 to-rose-600",
        icon: "🚑"
      }
    ],
    // Travel/International Set
    [
      {
        href: "#",
        title: "Medical Tourism Guide",
        description: "Surgery abroad for 90% less",
        price: "$19.99",
        image: null,
        bgColor: "bg-gradient-to-br from-violet-500 to-purple-600",
        icon: "✈️"
      },
      {
        href: "#",
        title: "Travel Insurance",
        description: "Global health coverage",
        price: "$45/month",
        image: null,
        bgColor: "bg-gradient-to-br from-teal-500 to-cyan-600",
        icon: "🌍"
      },
      {
        href: "#",
        title: "Language Translator",
        description: "Medical terms in 50+ languages",
        price: "$12.99",
        image: null,
        bgColor: "bg-gradient-to-br from-pink-500 to-rose-600",
        icon: "🌐"
      }
    ]
  ];

  const handleSearch = (query: string, type: 'hospital' | 'procedure') => {
    // Simple search simulation
    if (type === 'hospital') {
      const results = allWorstOffenders.filter(h => 
        h.name.toLowerCase().includes(query.toLowerCase()) ||
        h.city.toLowerCase().includes(query.toLowerCase()) ||
        h.state.toLowerCase().includes(query.toLowerCase())
      );
      setSearchResults(results);
    }
  };

  // Cycle through ad sets every 15 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentAdSet(prev => (prev + 1) % adSets.length);
    }, 15000);

    return () => clearInterval(interval);
  }, [adSets.length]);

  // Detect if running as PWA
  useEffect(() => {
    const isPWAMode = window.matchMedia('(display-mode: standalone)').matches ||
                      (window.navigator as any).standalone === true;
    setIsPWA(isPWAMode);
  }, []);

  // PWA Install prompt handler
  useEffect(() => {
    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowInstallButton(true);
    };

    window.addEventListener('beforeinstallprompt', handler);

    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) {
      // Fallback for browsers that don't support PWA install
      alert('To install this app:\n\n1. Tap the Share button\n2. Select "Add to Home Screen"\n\nOr use Chrome/Edge browser for one-click install.');
      return;
    }

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;

    if (outcome === 'accepted') {
      console.log('User accepted the install prompt');
    }

    setDeferredPrompt(null);
    setShowInstallButton(false);
  };

  const handleNotifySubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!notifyEmail) {
      alert('Please enter a valid email address');
      return;
    }

    try {
      // TODO: Save email to database for notifications
      console.log('Notify email:', notifyEmail);
      alert('Thank you! We will notify you when the app is available.');
      setShowAppNotifyModal(false);
      setNotifyEmail('');
    } catch (error) {
      console.error('Error saving email:', error);
      alert('Failed to save email. Please try again.');
    }
  };

  // Luigi's Hall of Shame homepage
  return (
    <div className="min-h-screen bg-background pb-32 relative">
      <BrandTheme />
      <HomeSEO />
      <BackgroundMedia />

      {/* PWA Refresh Button - Only shown when installed as PWA */}
      {isPWA && (
        <button
          onClick={() => window.location.reload()}
          className="fixed top-4 right-4 z-50 p-3 bg-matrix-green/20 hover:bg-matrix-green/30 border border-matrix-green/40 rounded-full transition-all backdrop-blur-sm"
          title="Refresh App"
        >
          <RefreshCw className="w-5 h-5 text-matrix-green" />
        </button>
      )}

      {/* Ad Layout Container - Only visible on XL screens */}
      <div className="hidden xl:flex xl:fixed xl:left-0 xl:top-20 xl:bottom-0 xl:w-[300px] xl:p-4 xl:z-[5]">
        <div className="w-full">
          <div className="sticky top-24">
            <AdSlot
              slot="1234567890"
              format="vertical"
              className="w-full"
            />
          </div>
        </div>
      </div>

      <div className="hidden xl:flex xl:fixed xl:right-0 xl:top-20 xl:bottom-0 xl:w-[300px] xl:p-4 xl:z-[5]">
        <div className="w-full">
          <div className="sticky top-24">
            <AdSlot
              slot="0987654321"
              format="vertical"
              className="w-full"
            />
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="xl:mx-[300px]">
        {/* Hero Section */}
        <section className="relative min-h-screen flex items-center justify-center z-10 pb-24">

        {/* Hero Content */}
        <div className="relative z-10 text-center max-w-6xl mx-auto px-4 sm:px-6">
          <div className="mb-8 sm:mb-10" style={{ paddingTop: '50px' }}>
            <h1 className="text-3xl xs:text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-bold mb-5 sm:mb-6 glitch leading-tight break-words" data-text={brand.name}>
              <span className="text-blood-red">{brand.messaging.heroTitle.primary}</span>{" "}
              <span className="text-matrix-green">{brand.messaging.heroTitle.secondary}</span>
            </h1>
            <p className="text-sm xs:text-base sm:text-lg md:text-xl lg:text-2xl xl:text-3xl text-terminal-green font-mono mb-4 sm:mb-5 px-2">
              {brand.messaging.heroSubtitle}
            </p>
            <p className="text-sm sm:text-base md:text-lg lg:text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed px-2">
              {brand.messaging.heroDescription}
            </p>
          </div>

          {/* How We Got Here - Inline in Hero */}
          <div className="mb-8 sm:mb-12 max-w-4xl mx-auto px-3 sm:px-2">
            <div className="terminal-card p-4 sm:p-4 lg:p-6 xl:p-8 text-center">
              <h2 className="text-xl sm:text-2xl lg:text-3xl xl:text-4xl font-bold text-blood-red mb-4 sm:mb-4 lg:mb-6 glitch" data-text="How We Got Here">
                How We Got Here
              </h2>
              <div className="space-y-3 sm:space-y-3 lg:space-y-4 text-sm sm:text-base lg:text-lg leading-relaxed">
                <p className="text-foreground">
                  Hospitals once existed as <span className="text-matrix-green font-mono">sacred institutions</span> -
                  non-profit sanctuaries dedicated to healing humanity. Doctors took the <span className="text-matrix-green font-mono">Hippocratic Oath</span>,
                  swearing to "first, do no harm" and serve patients above all else.
                </p>
                <p className="text-foreground">
                  But unchecked capitalism has <span className="text-blood-red font-mono">corrupted the code</span>.
                  The healing oath has been replaced with profit margins. Hospital "non-profits" now hoard billions
                  while patients die from rationing insulin they can't afford.
                </p>
                <p className="text-foreground">
                  <span className="text-matrix-green font-mono">Wake up, America.</span>
                  Laissez-faire free markets have <span className="text-blood-red font-mono glitch" data-text="FAILED">FAILED</span> in 2025.
                  This website exposes the truth: you can get the same medical care for <span className="text-matrix-green font-mono">1/10th the price</span>
                  in almost any other developed nation.
                </p>
                <p className="text-sm sm:text-base lg:text-lg xl:text-xl font-bold text-matrix-green font-mono">
                  The system isn't broken. It's working exactly as designed - for them, not you.
                </p>
              </div>
            </div>
          </div>

          {/* Live Counter */}
          <div className="mb-8 sm:mb-12">
            <LiveCounter
              initialAmount={dailyOvercharges}
              incrementPerSecond={3287} 
            />
          </div>

          {/* Three Search Bars */}
          <div className="space-y-6 sm:space-y-5 mb-8 sm:mb-8 px-2">
            {/* Hospitals Search */}
            <div className="w-full">
              <div className="text-base sm:text-base text-gray-200 mb-2 font-mono font-bold tracking-wide">Search Hospitals</div>
              <SearchBarWithAutocomplete
                placeholder="Search hospitals by name or location..."
                autoFocus={false}
                initialSearchType="hospital"
              />
            </div>

            {/* Medical Codes Search */}
            <div className="w-full">
              <div className="text-base sm:text-base text-gray-200 mb-2 font-mono font-bold tracking-wide">Search Medical Codes</div>
              <MedicalCodeSearch
                placeholder="CPT, DRG, REV, RX, or SUP codes..."
                autoFocus={false}
              />
            </div>

            {/* Scenarios Search */}
            <div className="w-full">
              <div className="text-base sm:text-base text-gray-200 mb-2 font-mono font-bold tracking-wide">Search Medical Scenarios</div>
              <ScenarioSearch
                placeholder="Common medical scenarios..."
                autoFocus={false}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Today's Worst Offenders */}
      <WorstOffendersSection />

      {/* Real-Time Analytics Section */}
      <PricingAnalyticsSummary />

      {/* Common Medical Scenarios */}
      <ScenariosPreview />

      {/* System Corruption Notice */}
      <section className="relative py-12 sm:py-16 lg:py-20 px-4 sm:px-6 z-10">
        <div className="max-w-4xl mx-auto text-center">
          <div className="terminal-card p-6 sm:p-8 lg:p-12 corruption">
            <Zap className="w-14 h-14 sm:w-16 sm:h-16 text-blood-red mx-auto mb-5 sm:mb-6 animate-pulse" />
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-blood-red mb-5 sm:mb-6 glitch" data-text={brand.messaging.corruptionTitle}>
              {brand.messaging.corruptionTitle}
            </h2>
            <p className="text-base sm:text-lg lg:text-xl text-foreground mb-7 sm:mb-8 font-mono leading-relaxed">
              {brand.messaging.corruptionDescription}
            </p>
            <Link to="/search">
              <Button size="lg" className="px-10 sm:px-12 py-4 sm:py-5 text-base sm:text-lg font-bold">
                <Database className="w-5 h-5 sm:w-6 sm:h-6 mr-2" />
                {brand.messaging.callToAction}
                <ArrowRight className="w-5 h-5 sm:w-6 sm:h-6 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
      </div>
      {/* End Main Content */}

      {/* Recent Educational Guides - Full Width */}
      <RecentGuidesSwiper />

      {/* Watermark Logo - Hidden on mobile, fixed on desktop (Luigi's only).
          appIconUrl resolves per brand via BrandConfig so this isn't tied
          to luigi's CDN — and the brand.key gate above keeps it
          AHP-suppressed regardless. */}
      {brand.key !== 'ahp' && (
        <img
          src={brand.appIconUrl}
          alt={`${brand.name} Logo`}
          className="hidden md:block"
          style={{
            position: 'fixed',
            right: 0,
            bottom: '160px',
            opacity: 0.5,
            maxHeight: '50vh',
            zIndex: 1,
            borderRadius: '5vw',
            pointerEvents: 'none'
          }}
        />
      )}

      {/* App Download Buttons - Fixed on desktop, static on mobile */}
      <div className="md:fixed relative md:right-2 lg:right-5 md:bottom-[90px] lg:bottom-[100px] md:z-10 z-10 flex flex-col sm:flex-row gap-3 px-4 py-8 md:px-0 md:py-0 justify-center md:justify-end mb-8 md:mb-0">
        {/* Mobile Section Title */}
        <div className="md:hidden w-full text-center mb-4">
          <h3 className="text-lg font-bold text-matrix-green font-mono">Download the App</h3>
          <p className="text-sm text-muted-foreground mt-1">Get instant price comparisons on mobile</p>
        </div>

        {/* Button Container */}
        <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
          {/* iOS App Store Button */}
          <button
            onClick={() => setShowAppNotifyModal(true)}
            className="flex items-center justify-center gap-2 sm:gap-3 px-4 sm:px-4 py-3 sm:py-2 bg-black/80 hover:bg-black text-white rounded-lg transition-all border border-white/20 backdrop-blur-sm opacity-80 hover:opacity-100 w-full sm:w-auto"
          >
          <svg className="w-6 h-6 sm:w-8 sm:h-8 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor">
            <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
          </svg>
          <div className="text-left">
            <div className="text-[9px] sm:text-[10px] leading-none opacity-80">Download on the</div>
            <div className="text-xs sm:text-sm font-semibold leading-tight">App Store</div>
          </div>
        </button>

          {/* Android Google Play Button */}
          <button
            onClick={() => setShowAppNotifyModal(true)}
            className="flex items-center justify-center gap-2 sm:gap-3 px-4 sm:px-4 py-3 sm:py-2 bg-black/80 hover:bg-black text-white rounded-lg transition-all border border-white/20 backdrop-blur-sm opacity-80 hover:opacity-100 w-full sm:w-auto"
          >
          <svg className="w-6 h-6 sm:w-8 sm:h-8 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor">
            <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.5,12.92 20.16,13.19L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/>
          </svg>
          <div className="text-left">
            <div className="text-[9px] sm:text-[10px] leading-none opacity-80">GET IT ON</div>
            <div className="text-xs sm:text-sm font-semibold leading-tight">Google Play</div>
          </div>
        </button>

          {/* Web PWA Install Button */}
          <button
            onClick={handleInstallClick}
            className="flex items-center justify-center gap-2 sm:gap-3 px-4 sm:px-4 py-3 sm:py-2 bg-matrix-green/80 hover:bg-matrix-green/90 text-black rounded-lg transition-all border border-matrix-green/40 backdrop-blur-sm opacity-80 hover:opacity-100 w-full sm:w-auto"
          >
          <svg className="w-6 h-6 sm:w-8 sm:h-8 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12,2A10,10 0 0,1 22,12A10,10 0 0,1 12,22A10,10 0 0,1 2,12A10,10 0 0,1 12,2M12,4A8,8 0 0,0 4,12A8,8 0 0,0 12,20A8,8 0 0,0 20,12A8,8 0 0,0 12,4M11,16.5L6.5,12L7.91,10.59L11,13.67L16.59,8.09L18,9.5L11,16.5Z"/>
          </svg>
          <div className="text-left">
            <div className="text-[9px] sm:text-[10px] leading-none opacity-80">Install</div>
            <div className="text-xs sm:text-sm font-semibold leading-tight">Web App</div>
          </div>
        </button>
        </div>
      </div>

      <DataSourceAttribution />

      {/* App Notification Modal */}
      {showAppNotifyModal && (
        <div
          className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setShowAppNotifyModal(false)}
        >
          <div
            className="bg-background border border-matrix-green/30 rounded-lg p-6 max-w-md w-full shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-2xl font-bold text-matrix-green mb-4">App Coming Soon!</h2>
            <p className="text-foreground mb-6">
              App is currently pending approval to the app store. Please enter your email and we will notify you when it's launched.
            </p>

            <form onSubmit={handleNotifySubmit} className="space-y-4">
              <div>
                <input
                  type="email"
                  value={notifyEmail}
                  onChange={(e) => setNotifyEmail(e.target.value)}
                  placeholder="Enter your email address"
                  className="w-full px-4 py-3 bg-secondary border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-matrix-green transition-colors"
                  required
                />
              </div>

              <div className="flex gap-3">
                <Button
                  type="submit"
                  className="flex-1 bg-matrix-green hover:bg-matrix-green/90 text-black font-semibold"
                >
                  Notify Me
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowAppNotifyModal(false)}
                  className="flex-1 border-matrix-green/30 text-matrix-green hover:bg-matrix-green/10"
                >
                  Cancel
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Index;
